import { useCallback } from "react";

import { Action, Module, Run, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";

import { isRunReviewable } from "../useReviewRun/accessValidation";
import useHasEntityWriteAccess from "./useHasEntityWriteAccess";

const REVIEW_ACCESS_TYPES = [Action.RunReview];

const hasReviewAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(REVIEW_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can review a run.
 * A user needs either write access to the entity or the RunReview permission to review a run.
 * @returns {(run?: Run, entity?: Module | Stack) => AccessResult} Function that takes an optional run and entity and returns a AccessResult indicating if user can review the run
 */
const useCanReviewRun = (): ((run?: Run, entity?: Module | Stack) => AccessResult) => {
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const { viewer } = useTypedContext(AccountContext);
  return useCallback(
    (run, entity) => {
      if (!isRunReviewable(run)) {
        return { result: false };
      }

      if (!entity) {
        return { result: false };
      }

      const hasAccessToReview =
        hasEntityWriteAccess(entity) ||
        hasReviewAccess(viewer, entity?.spaceDetails?.id || entity?.space);

      if (hasAccessToReview) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasEntityWriteAccess, viewer]
  );
};

export default useCanReviewRun;
